export type BlogSection =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'list'; items: string[] }
  | { type: 'stats'; items: { value: string; label: string }[] }
  | { type: 'callout'; text: string; href?: string; linkLabel?: string }
  | { type: 'divider' };

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  location: string;
  publishedAt: string; // ISO date string
  tags: string[];
  readingTime: number; // minutes
  coverLabel?: string; // small top label on index card
  sections: BlogSection[];
}

export const posts: BlogPost[] = [
  {
    slug: 'short-let-planning-edinburgh',
    title: 'Short-Let Planning in Edinburgh: What the Data Shows',
    excerpt:
      "Since 2022, Edinburgh's short-let crackdown has produced a striking dataset. Here's what the City Scope pipeline reveals about how planning policy is being applied in practice.",
    author: 'Harry Williams',
    location: 'Edinburgh',
    publishedAt: '2025-03-28',
    tags: ['Short-Term Lets', 'Planning Data', 'Edinburgh'],
    readingTime: 4,
    coverLabel: 'Short-Let Watch',
    sections: [
      {
        type: 'paragraph',
        text: "I've recently published a new part of City Scope focused on short-term let planning applications in Edinburgh.",
      },
      {
        type: 'paragraph',
        text: "Since 2022, short-term let operators in Scotland have needed to register with their local authority and obtain the correct permissions to operate. In Edinburgh, that has meant many landlords also needing planning permission for full-time short-term let use.",
      },
      {
        type: 'paragraph',
        text: "A lot of operators appear to have assumed that if a property had already been used historically as a short-term let, planning permission would not be required. In many cases, that has not proved to be true. As a result, a large number of retrospective planning applications have had to be submitted by landlords wanting to continue operating.",
      },
      {
        type: 'paragraph',
        text: "Using the City Scope planning data pipeline, I'm now pulling in short-term let applications directly from the planning portal alongside the rest of the city's planning activity. The system currently retrieves new applications three times a day, and the short-term let dataset currently runs from January 2025 to today.",
      },
      {
        type: 'heading',
        text: 'What you can do with it',
      },
      {
        type: 'paragraph',
        text: 'This means you can now use City Scope to:',
      },
      {
        type: 'list',
        items: [
          'Search for short-term let planning applications on your street',
          'Explore application hotspots on the planning map',
          'Filter short-term let applications across Edinburgh',
          'Review application outcomes in one place',
        ],
      },
      {
        type: 'callout',
        text: "I've built a dedicated reporting page that brings all of this together — including a live map, street search, and decision breakdown.",
        href: '/short-lets-report',
        linkLabel: 'Open the Short-Let Report →',
      },
      {
        type: 'heading',
        text: 'Early findings',
      },
      {
        type: 'paragraph',
        text: 'Some initial findings from the January 2025 onward dataset are striking:',
      },
      {
        type: 'stats',
        items: [
          { value: '84%', label: 'of decided applications refused' },
          { value: '264', label: 'applications in EH1 alone' },
          { value: '464', label: 'applications refused since Jan 2025' },
        ],
      },
      {
        type: 'paragraph',
        text: "This is still an early-stage dataset rather than a full historical picture, but it already gives a much clearer view of how short-term let policy is being applied in practice across the city.",
      },
      {
        type: 'heading',
        text: 'What comes next',
      },
      {
        type: 'paragraph',
        text: "Over time, I plan to extend the dataset further and add deeper application-level insights as I continue improving the parsing pipeline and reducing processing costs. The aim is simple: make local planning data easier to search, understand, and use.",
      },
    ],
  },
];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}
